import React, { useEffect, useState } from 'react';
import {
  collection, doc, getDoc, getDocs, query, where, orderBy, limit,
} from 'firebase/firestore';
import {
  ChevronRight, FileText, Calendar, DollarSign, Tag, ExternalLink, AlertTriangle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { useCompany } from '../../context/CompanyContext';

/**
 * Phase 1 / B1 — Inbox Lead Context Strip
 *
 * A 1-line read-only strip that surfaces what an operator needs to know about
 * the contact on the other end of the thread BEFORE they reply: stage, last
 * quote (status + amount), next scheduled visit, and any open invoice.
 *
 * The strip:
 *  • Renders only when the conversation has an attached lead (anchor.id).
 *  • Uses lightweight one-shot reads (not snapshots) — reloads on convo switch.
 *  • Fails open: any data error simply hides that chip; never blocks the UI.
 */

// ───────────────────────────────────────────────────────────────────────────
// helpers
// ───────────────────────────────────────────────────────────────────────────

const safeDate = (v) => {
  if (!v) return null;
  try {
    if (v?.toDate) return v.toDate();
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  } catch { return null; }
};

const formatShortDate = (d) => {
  if (!d) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  return v >= 1000
    ? `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`
    : `$${v.toFixed(0)}`;
};

const STAGE_TONE = {
  'New Lead':   'bg-blue-50 text-blue-700 border-blue-100',
  'Quoted':     'bg-orange-50 text-orange-700 border-orange-100',
  'Booked':     'bg-emerald-50 text-emerald-700 border-emerald-100',
  'Scheduled':  'bg-emerald-50 text-emerald-700 border-emerald-100',
  'Completed':  'bg-violet-50 text-violet-700 border-violet-100',
  'Lost':       'bg-gray-50 text-gray-500 border-gray-200',
};

const Chip = ({ icon: Icon, tone = 'bg-gray-50 text-gray-700 border-gray-200', children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[11px] font-medium leading-none whitespace-nowrap shrink-0 ${tone} ${onClick ? 'hover:brightness-95 cursor-pointer' : 'cursor-default'}`}
  >
    {Icon && <Icon className="w-3 h-3" />}
    <span className="truncate max-w-[160px]">{children}</span>
    {onClick && <ChevronRight className="w-3 h-3 opacity-50" />}
  </button>
);

// ───────────────────────────────────────────────────────────────────────────
// Component
// ───────────────────────────────────────────────────────────────────────────

const LeadContextStrip = ({ conversation }) => {
  const { companyId } = useCompany();
  const navigate = useNavigate();

  const [ctx, setCtx] = useState({
    leadId: null,
    stage: '',
    flashNote: '',     // propertyDetails.flashNote — shown as bright pill before stage
    lastQuote: null,    // {id, amount, status, createdAt}
    nextVisit: null,    // {id, scheduledStart}
    openInvoice: null,  // {id, amount, status}
    loading: true,
  });

  const leadId = conversation?.anchor?.id || conversation?.anchorId || null;
  const isLead = (conversation?.anchor?.type || (leadId ? 'lead' : '')) === 'lead';

  useEffect(() => {
    let cancelled = false;
    if (!companyId || !leadId || !isLead) {
      setCtx((c) => ({ ...c, loading: false, leadId: null }));
      return undefined;
    }

    setCtx((c) => ({ ...c, loading: true, leadId }));

    const co = collection.bind(null);
    const tasks = ['companies', companyId, 'tasks'];
    const quotes = ['companies', companyId, 'quotes'];
    const invoices = ['companies', companyId, 'invoices'];
    const leadRef = doc(db, 'companies', companyId, 'leads', leadId);

    (async () => {
      // Stage + flashNote from lead doc
      let stage = '';
      let flashNote = '';
      try {
        const ls = await getDoc(leadRef);
        if (ls.exists()) {
          const ld = ls.data() || {};
          stage = ld.stage || '';
          flashNote = (ld.propertyDetails?.flashNote || '').trim();
        }
      } catch { /* ignore */ }

      // Latest quote
      let lastQuote = null;
      try {
        const qs = await getDocs(
          query(
            co(db, ...quotes),
            where('leadId', '==', leadId),
            orderBy('createdAt', 'desc'),
            limit(1),
          ),
        );
        if (!qs.empty) {
          const d = qs.docs[0];
          const data = d.data() || {};
          const amount = (data.totals?.grandTotal) ?? data.total ?? 0;
          lastQuote = {
            id: d.id,
            amount,
            status: data.status || 'draft',
            createdAt: safeDate(data.createdAt),
          };
        }
      } catch { /* ignore — fail-open chip suppression */ }

      // Next scheduled visit (job task with scheduledStart in the future)
      let nextVisit = null;
      try {
        const now = new Date();
        const ts = await getDocs(
          query(
            co(db, ...tasks),
            where('leadId', '==', leadId),
            where('type', '==', 'job'),
            orderBy('scheduledStart', 'asc'),
            limit(8),
          ),
        );
        for (const td of ts.docs) {
          const d = td.data() || {};
          const start = safeDate(d.scheduledStart);
          if (start && start.getTime() >= now.getTime() - 60 * 60 * 1000) {
            nextVisit = { id: td.id, scheduledStart: start };
            break;
          }
        }
      } catch { /* ignore */ }

      // Open invoice (status not paid)
      let openInvoice = null;
      try {
        const inv = await getDocs(
          query(
            co(db, ...invoices),
            where('leadId', '==', leadId),
            limit(20),
          ),
        );
        for (const id_ of inv.docs) {
          const d = id_.data() || {};
          const status = (d.status || '').toLowerCase();
          if (status !== 'paid' && status !== 'voided' && status !== 'cancelled') {
            openInvoice = {
              id: id_.id,
              status,
              amount: d.balance ?? d.amountDue ?? d.amount ?? d.total ?? 0,
            };
            break;
          }
        }
      } catch { /* ignore */ }

      if (!cancelled) {
        setCtx({ leadId, stage, flashNote, lastQuote, nextVisit, openInvoice, loading: false });
      }
    })();

    return () => { cancelled = true; };
  }, [companyId, leadId, isLead]);

  if (!leadId || !isLead) return null;
  if (ctx.loading) {
    return (
      <div
        className="flex items-center gap-2 px-4 py-1.5 border-b border-gray-100 bg-gray-50/60 text-[11px] text-gray-400"
        data-testid="lead-context-strip-loading"
      >
        Loading lead context…
      </div>
    );
  }

  const stageTone = STAGE_TONE[ctx.stage] || 'bg-gray-50 text-gray-600 border-gray-200';
  const hasAny = ctx.flashNote || ctx.stage || ctx.lastQuote || ctx.nextVisit || ctx.openInvoice;
  if (!hasAny) return null;

  return (
    <div
      className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 bg-gray-50/60 overflow-x-auto"
      data-testid="lead-context-strip"
    >
      {ctx.flashNote && (
        <Chip
          icon={AlertTriangle}
          tone="bg-amber-50 text-amber-800 border-amber-200"
          onClick={() => navigate(`/leads/${leadId}`)}
          testId="lead-context-flash"
        >
          {ctx.flashNote}
        </Chip>
      )}

      {ctx.stage && (
        <Chip
          icon={Tag}
          tone={stageTone}
          onClick={() => navigate(`/leads/${leadId}`)}
          testId="lead-context-stage"
        >
          {ctx.stage}
        </Chip>
      )}

      {ctx.lastQuote && (
        <Chip
          icon={FileText}
          tone={
            ctx.lastQuote.status === 'approved'
              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
              : 'bg-orange-50 text-orange-700 border-orange-100'
          }
          onClick={() => navigate(`/leads/${leadId}?tab=quotes`)}
          testId="lead-context-quote"
        >
          {`Quote ${fmtMoney(ctx.lastQuote.amount)} · ${ctx.lastQuote.status}`}
        </Chip>
      )}

      {ctx.nextVisit && (
        <Chip
          icon={Calendar}
          tone="bg-blue-50 text-blue-700 border-blue-100"
          onClick={() => navigate('/calendar')}
          testId="lead-context-next-visit"
        >
          Next visit · {formatShortDate(ctx.nextVisit.scheduledStart)}
        </Chip>
      )}

      {ctx.openInvoice && (
        <Chip
          icon={DollarSign}
          tone="bg-rose-50 text-rose-700 border-rose-100"
          onClick={() => navigate('/invoices')}
          testId="lead-context-open-invoice"
        >
          {`Invoice ${fmtMoney(ctx.openInvoice.amount)} · ${ctx.openInvoice.status || 'unpaid'}`}
        </Chip>
      )}

      {/* deep-link to lead profile (text-only, far right) */}
      <button
        type="button"
        onClick={() => navigate(`/leads/${leadId}`)}
        className="ml-auto inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800 shrink-0"
        data-testid="lead-context-open-lead"
      >
        Open lead <ExternalLink className="w-3 h-3" />
      </button>
    </div>
  );
};

export default LeadContextStrip;
